import type { Contract, ContractCreateRequest, ContractDetailsRequest, ContractListQuery, ContractSortKey, ContractStatus } from "@shyne/shared/api";
import { daily_work_status } from "@/models/daily-work.model";
import type { DailyWorkJoin } from "@/repositories/daily-work.repository";

export type ContractDetailsChange = {
  status?: daily_work_status;
  start_date?: string | null;
  end_date?: string | null;
  monthly_price?: number | null;
};

export type NewContractRecord = ContractDetailsChange & { property: string; vendor: string; start_date: string };

export type ContractListFilters = {
  status?: daily_work_status | "";
  search?: string;
  inEffectFrom?: string;
  inEffectTo?: string;
};

const STATUS_OF_CONTRACT: Record<ContractStatus, daily_work_status> = {
  active: daily_work_status.active,
  paused: daily_work_status.paused,
  cancelled: daily_work_status.cancelled,
};

const SORT_KEY_OF_COLUMN: Record<string, ContractSortKey> = {
  created_at: "createdAt",
  "properties.name": "property",
  start_date: "start",
  end_date: "end",
  monthly_price: "monthlyPrice",
};

export const toContractRecord = (contract: Contract): DailyWorkJoin => ({
  id: contract.id,
  vendor: { id: contract.vendor.id, display_name: contract.vendor.name },
  property: { id: contract.property.id, name: contract.property.name, address: contract.property.address, image: contract.property.image },
  status: STATUS_OF_CONTRACT[contract.status],
  start_date: contract.start,
  end_date: contract.end,
  monthly_price: contract.monthlyPrice ?? null,
  created_at: contract.createdAt,
});

export function toContractListQuery(
  { status, search, inEffectFrom, inEffectTo }: ContractListFilters,
  { page, pageSize, sortKey, sortOrder, actingOrganization }: { page: number; pageSize: number; sortKey: string; sortOrder: "asc" | "desc"; actingOrganization: string },
): Partial<ContractListQuery> {
  const searched = search?.trim();
  return {
    actingOrganization,
    ...(status ? { status } : {}),
    ...(searched ? { search: searched } : {}),
    ...(inEffectFrom ? { inEffectFrom } : {}),
    ...(inEffectTo ? { inEffectTo } : {}),
    sort: SORT_KEY_OF_COLUMN[sortKey] ?? "createdAt",
    order: sortOrder,
    page,
    pageSize,
  };
}

const defined = <T extends Record<string, unknown>>(value: T): Partial<T> =>
  Object.fromEntries(Object.entries(value).filter(([, field]) => field !== undefined)) as Partial<T>;

export const toContractCreateRequest = ({ property, vendor, status, start_date, end_date, monthly_price }: NewContractRecord): ContractCreateRequest => ({
  actingOrganization: vendor,
  property,
  start: start_date,
  ...defined({ end: end_date, monthlyPrice: monthly_price, status }),
});

export function toContractDetailsRequest({ status, start_date, end_date, monthly_price }: ContractDetailsChange, actingOrganization: string): ContractDetailsRequest {
  if (start_date === null) throw new Error("A contract needs a start date.");
  return { actingOrganization, ...defined({ status, start: start_date, end: end_date, monthlyPrice: monthly_price }) };
}
